import React, { useState, useRef, useEffect } from 'react'
import { Pencil } from 'lucide-react'
import { useFlavorManager } from '@cannect/hooks/useFlavorManager'

interface Flavor {
  id: number
  flavor: string
  mainProduct?: number
  balance?: number
  total?: number
  name?: string
}

interface FlavorSelectProps {
  productId: number
  mainProductId?: number | undefined
  value: number | undefined
  currentFlavor?: any
  flavors?: Flavor[]
  onChange: (flavorId: number) => void
  disabled?: boolean
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
  autoFetch?: boolean
  preloadOnMount?: boolean
  showButtonWhenNoFlavors?: boolean
  minFlavorsToShowButton?: number
}

const FlavorSelect: React.FC<FlavorSelectProps> = ({ 
  productId, 
  mainProductId,
  value, 
  currentFlavor, 
  flavors: propFlavors, 
  onChange, 
  disabled, 
  open: openProp, 
  onOpen, 
  onClose,
  autoFetch = true,
  preloadOnMount = true,
  showButtonWhenNoFlavors = false,
  minFlavorsToShowButton = 2
}) => {
  const [openInternal, setOpenInternal] = useState(false)
  const open = openProp !== undefined ? openProp : openInternal;
  const ref = useRef<HTMLDivElement>(null)
  
  const { 
    fetchProductFlavors, 
    getProductFlavors, 
    loadingFlavors, 
    preloadProductFlavors, 
    shouldShowFlavorButton, 
    getFlavorCount 
  } = useFlavorManager()
  const [fetchedFlavors, setFetchedFlavors] = useState<Flavor[]>([])
  const [hasFetched, setHasFetched] = useState<Record<number, boolean>>({})
  const [isPreloading, setIsPreloading] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom')
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(200)

  useEffect(() => {
    if (preloadOnMount && productId && !propFlavors?.length) {
      const productIdToFetch = mainProductId || productId
      const cachedFlavors = getProductFlavors(productIdToFetch)
      
      if (cachedFlavors.length > 0 || loadingFlavors[productIdToFetch] || hasFetched[productIdToFetch]) {
        return
      }

      const preloadFlavors = async () => {
        setIsPreloading(true)
        setHasFetched(prev => ({ ...prev, [productIdToFetch]: true }))
        try {
          const flavors = await preloadProductFlavors(productIdToFetch, mainProductId)
          setFetchedFlavors(flavors)
        } catch (error) {
          console.error('Erro ao pré-carregar sabores:', error)
        } finally {
          setIsPreloading(false)
        }
      }

      preloadFlavors()
    }
  }, [preloadOnMount, productId, mainProductId, propFlavors, getProductFlavors, loadingFlavors, hasFetched, preloadProductFlavors])
  
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      const minSpaceNeeded = 300

      if (spaceBelow < minSpaceNeeded && spaceAbove > spaceBelow) {
        setDropdownPosition('top')
        setDropdownMaxHeight(Math.min(spaceAbove - 20, 200))
      } else {
        setDropdownPosition('bottom')
        setDropdownMaxHeight(Math.min(spaceBelow - 20, 200))
      }
    }
  }, [open])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        if (openProp === undefined) setOpenInternal(false);
        if (onClose) onClose();
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [openProp, onClose])

  const handleOpen = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    if (openProp === undefined) setOpenInternal(o => !o);
    if (onOpen) onOpen();

    if (
      autoFetch && 
      productId && 
      !propFlavors?.length && 
      !loadingFlavors[productId] && 
      !hasFetched[productId]
    ) {
      setHasFetched(prev => ({ ...prev, [productId]: true }))
      console.log('fetchProductFlavors', productId, mainProductId)
      const productIdToFetch = !mainProductId ? productId : mainProductId
      console.log('productIdToFetch', productIdToFetch)
      if (!productIdToFetch) return
      const flavors = await fetchProductFlavors(productIdToFetch)
      console.log('flavors', flavors)
      setFetchedFlavors(flavors)
    }
  };


  const productIdToUse = mainProductId || productId
  const cachedFlavors = getProductFlavors(productIdToUse)
  const flavors = propFlavors && propFlavors.length > 0 
    ? propFlavors 
    : fetchedFlavors.length > 0 
    ? fetchedFlavors 
    : cachedFlavors
  
  const selectedFlavor = flavors.find(f => Number(f.id) === Number(value));
  const availableFlavors = flavors.filter(f => 
    f.flavor && 
    f.flavor !== 'Sabor' && 
    Number(f.id) !== Number(value)
  );
  
  const displayFlavor = selectedFlavor?.flavor || currentFlavor || 'Selecione';
  
  const shouldShowButton = showButtonWhenNoFlavors || 
    (availableFlavors.length >= minFlavorsToShowButton) ||
    shouldShowFlavorButton(productIdToUse);

  const isLoadingInitial = isPreloading || loadingFlavors[productIdToUse];

  const renderDropdown = () => {
    if (!open) return null

    const positionClass = dropdownPosition === 'top' 
      ? 'bottom-full mb-1' 
      : 'top-full mt-1'

    return (
      <div 
        className={`absolute ${positionClass} left-0 z-[9999] min-w-[200px] max-w-[300px] rounded-md border border-gray-200 bg-white shadow-lg`}
        style={{ maxHeight: dropdownMaxHeight }}
      >
        {loadingFlavors[productIdToUse] || isLoadingInitial ? (
          <div className="px-4 py-2 text-sm text-gray-500">Carregando sabores...</div>
        ) : availableFlavors.length > 0 ? (
          <div className="overflow-y-auto" style={{ maxHeight: dropdownMaxHeight - 8 }}>
            {availableFlavors.map(flavor => (
              <button
                key={flavor.id}
                className={`block w-full px-4 py-2 text-left border-none text-sm hover:bg-gray-100 transition-colors ${
                  Number(flavor.id) === Number(value) ? 'font-bold text-primary-600' : ''
                }`}
                onClick={() => {
                  onChange(flavor.id);
                  if (openProp === undefined) setOpenInternal(false);
                  if (onClose) onClose();
                }}
                disabled={disabled}
              >
                {flavor.flavor}
              </button>
            ))}
          </div>
        ) : (
          <div className="px-4 py-2 text-sm text-gray-500">Nenhum sabor disponível</div>
        )}
      </div>
    )
  }

  return (
    <div ref={ref} className="relative flex items-center overflow-visible rounded-full border border-gray-200 bg-gray-50 px-2">
      <div className="flex h-6 w-20 rounded-full items-center justify-center bg-gray-100 px-2 mr-4">
        <span className="text-xs text-neutral-500 bg-gray-100">Sabor</span>
      </div>
      <span className="text-xs text-neutral-700">{displayFlavor?.name || displayFlavor}</span>
      {shouldShowButton && (
        <button
          type="button"
          className="ml-2 p-1 border-none bg-transparent"
          onClick={handleOpen}
          disabled={disabled || isLoadingInitial}
          tabIndex={0}
          aria-label="Selecionar sabor"
          aria-expanded={open}
        >
          {isLoadingInitial ? (
            <div className="h-3 w-3 animate-spin border border-gray-300 border-t-primary-600 rounded-full" />
          ) : (
            <Pencil className="h-3 w-3 cursor-pointer text-neutral-500" />
          )}
        </button>
      )}
      {renderDropdown()}
    </div>
  )
}

export default FlavorSelect
